import { useEffect, useState } from "react";
import styled from "styled-components";
import { fetchWrapper } from "../../utils/apiHandlers";
import LoadingComponent from "../LoadingComponent";
import ProductInfoComp from "../ProductInfo";
import SmallButton from "../SmallButton";
import theme from "../../theme";

interface Prop {
  userid: string;
}

const ATTR_LABELS: { [key: string]: string } = {
  outdoor_seating: "Outdoor seating", dog_friendly: "Dog-friendly", reservations: "Reservations",
  parking: "Parking", kids: "Kids menu / family seating", vegetarian: "Vegetarian options",
  vegan: "Vegan options", gluten_free: "Gluten-free options", happy_hour: "Happy hour",
  private_events: "Private events", live_music: "Live music", delivery_takeout: "Takeout / delivery",
  wifi: "Wi-Fi", wheelchair: "Wheelchair access", brunch: "Brunch",
}

const ContentComp: React.FC<Prop> = ({ userid }) => {
  const [facts, setfacts] = useState<any>(null)
  const [factsat, setfactsat] = useState<any>(null)
  const [pages, setpages] = useState<{ url: string; count: number }[]>([])
  const [loading, setloading] = useState(false)

  const load = async () => {
    if (!userid) return
    setloading(true)
    try {
      let [factsRes, contextsRes]: any = await Promise.all([
        fetchWrapper.post("/api/context/getfacts", { userid }),
        fetchWrapper.post("/api/context/getcontexts", { userid }),
      ])
      setfacts(factsRes.facts)
      setfactsat(factsRes.factsat)
      let byUrl: { [key: string]: number } = {}
      for (const c of contextsRes.contexts || []) {
        if (!c.url) continue
        byUrl[c.url] = (byUrl[c.url] || 0) + 1
      }
      setpages(Object.entries(byUrl).map(([url, count]) => ({ url, count })).sort((a, b) => b.count - a.count))
    } catch (error) {
      // best-effort - the manual notes section below still works even if facts/pages fail to load
    }
    setloading(false)
  }

  useEffect(() => { load() }, [userid])

  const contactLine = () => {
    let c = facts?.contact || {}
    let parts = []
    if (c.email?.value) parts.push(c.email.value)
    if (c.phone?.value) parts.push(c.phone.value)
    return parts.join(" · ")
  }

  const attrEntries = Object.entries(facts?.attrs || {})

  return (
    <MainContainer>
      <TopRow>
        <Title>Content — what your bot knows</Title>
        <SmallButton text={loading ? "Loading…" : "Refresh"} handleClick={load} />
      </TopRow>

      {loading && <LoadingComponent />}

      <Columns>
        <Col style={{ flex: 1.3 }}>
          <SectionTitle>Extracted facts {factsat && <Muted>· last updated {new Date(factsat).toLocaleDateString()}</Muted>}</SectionTitle>

          {!facts && !loading && <EmptyCard>No facts extracted yet — they're built automatically the first time your site is crawled.</EmptyCard>}

          {facts && (
            <>
              <FactCard>
                <FactHead>
                  <FactName>Hours</FactName>
                  {facts.hours?.parsed && Object.keys(facts.hours.parsed).length ? <Verified>verified</Verified> : <NotStated>not stated on site</NotStated>}
                </FactHead>
              </FactCard>

              <FactCard>
                <FactHead>
                  <FactName>Contact</FactName>
                  {contactLine() ? <Verified>verified</Verified> : <NotStated>not found</NotStated>}
                </FactHead>
                {contactLine() && <FactValue>{contactLine()}</FactValue>}
              </FactCard>

              {attrEntries.map(([key, v]: any) => (
                <FactCard key={key}>
                  <FactHead>
                    <FactName>{ATTR_LABELS[key] || key}</FactName>
                    <Verified>{v.value === false ? "no" : "yes"}</Verified>
                  </FactHead>
                  <Quote>&ldquo;{v.quote}&rdquo;</Quote>
                </FactCard>
              ))}

              {!attrEntries.length && <EmptyCard>No attributes (outdoor seating, reservations, etc.) found in the crawled text yet.</EmptyCard>}
            </>
          )}
        </Col>

        <Col style={{ flex: 1 }}>
          <SectionTitle>Crawled pages {pages.length > 0 && <Muted>· {pages.length}</Muted>}</SectionTitle>
          {pages.length ? (
            <PagesCard>
              {pages.map((p) => (
                <PageRow key={p.url}>
                  <PageUrl href={p.url} target="_blank" rel="noreferrer">{p.url.replace(/^https?:\/\//, '').slice(0, 46)}</PageUrl>
                  <Muted>{p.count} chunk{p.count === 1 ? '' : 's'}</Muted>
                </PageRow>
              ))}
            </PagesCard>
          ) : <EmptyCard>No crawled pages yet.</EmptyCard>}

          <SectionTitle style={{ marginTop: 18 }}>Manual notes</SectionTitle>
          <Hint>Anything not on your site — add it below and it's treated the same as a crawled fact.</Hint>
          <ProductInfoComp userid={userid} />
        </Col>
      </Columns>
    </MainContainer>
  );
};

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${theme.colors.text};
`

const Columns = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  @media (max-width: 900px) {
    flex-direction: column;
  }
`

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  width: 100%;
`

const SectionTitle = styled.div`
  font-weight: 800;
  font-size: 14px;
  color: ${theme.colors.text};
`

const Muted = styled.span`
  font-weight: 500;
  font-size: 11px;
  color: ${theme.colors.textMuted};
`

const Hint = styled.div`
  font-size: 11px;
  color: ${theme.colors.textMuted};
`

const EmptyCard = styled.div`
  background: ${theme.colors.background};
  border: 1px dashed ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 12px;
  font-size: 12px;
  color: ${theme.colors.textMuted};
`

const FactCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FactHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const FactName = styled.div`
  font-weight: 800;
  font-size: 12px;
`

const FactValue = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
`

const Verified = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: ${theme.colors.success};
`

const NotStated = styled.span`
  font-size: 10px;
  color: ${theme.colors.textMuted};
`

const Quote = styled.div`
  font-size: 10px;
  background: ${theme.colors.background};
  border-radius: 6px;
  padding: 6px 8px;
  color: ${theme.colors.textMuted};
`

const PagesCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  overflow: hidden;
`

const PageRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 12px;
  font-size: 12px;
  border-bottom: 1px solid ${theme.colors.border};
  &:last-child { border-bottom: none; }
`

const PageUrl = styled.a`
  color: ${theme.colors.primary};
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover { text-decoration: underline; }
`

export default ContentComp;
